import React, { useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Drawer } from '@mui/material';
import { Menu, ChevronLeft, ChevronRight } from '@mui/icons-material';
import type {
  DRC_ASSESSMENT_MODULE,
  FRC_ASSESSMENT_MODULE,
  GS_ASSESSMENT_MODULE,
} from './constants';
import type { AR_MODULE, QUALIFICATION_MODULE } from '../../AcademicRegistry';
import { type SidebarData, Sidebar, IconButton } from '../../../components';
import { type INTERVIEW_PANEL_MODULE } from '../../InterviewManagement';

export type AssessmentSidebarProps = {
  collapsible?: boolean;
  onChangeActivated: (activatedId: string) => void;
  sidebarData: SidebarData[];
  drawerWidth?: number;
  defaultOpen?: boolean;
  drawerInnerWidth?: string; // rem
  currentActivatedModule: string;
  activatedModule?:
    | DRC_ASSESSMENT_MODULE
    | FRC_ASSESSMENT_MODULE
    | GS_ASSESSMENT_MODULE
    | AR_MODULE
    | QUALIFICATION_MODULE
    | INTERVIEW_PANEL_MODULE;
  buttonField?: React.ReactNode;
};

export const AssessmentSidebar: React.FunctionComponent<
  AssessmentSidebarProps
> = props => {
  const {
    onChangeActivated,
    collapsible = false,
    sidebarData,
    activatedModule,
    defaultOpen = true,
    drawerWidth = 280,
    drawerInnerWidth = '17rem',
    currentActivatedModule,
  } = props;

  const [open, setOpen] = useState(defaultOpen);
  const [defaultActivatedModule, setDefaultActivatedModule] = useState(
    activatedModule ?? sidebarData[0].id,
  );

  const drawerContentStyle: React.CSSProperties = {
    transition: 'padding-top 0.25s ease',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollBehavior: 'smooth',
    scrollbarWidth: 'thin',
    position: 'sticky',
  };

  const theme = useTheme();

  const handleDrawerOpen = (): void => {
    setOpen(true);
  };

  const handleDrawerClose = (): void => {
    setOpen(false);
  };
  const handleChangeActivated = (activatedId: string): void => {
    onChangeActivated(activatedId);
    setDefaultActivatedModule(activatedId);
  };

  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 3),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  }));

  return collapsible ? (
    <>
      <div
        style={{
          position: 'relative',
          left: '1.25rem',
          top: '1rem',
          width: '2.25rem',
        }}
      >
        <IconButton
          color="default"
          aria-label="open drawer"
          onClick={handleDrawerOpen}
          edge="start"
          sx={{ mr: 2, ...(open && { display: 'none' }), position: 'fixed' }}
          icon={<Menu />}
        />
      </div>
      <Drawer
        style={{
          top: '5rem',
          position: 'sticky',
          minHeight: '100vh',
          height: '100%',
        }}
        sx={{
          width: `${drawerWidth}px`,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: `${drawerWidth}px`,
            boxSizing: 'border-box',
          },
          '& .sc-jTrNMu': {
            width: '14rem',
          },
          '& .MuiPaper-root': {
            position: 'sticky',
            height: 'calc(100vh - 5rem)',
            top: '5rem',
          },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          flexGrow: 1,
          ...(!open && { display: 'none' }),
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <div style={drawerContentStyle}>
          <DrawerHeader>
            <IconButton
              onClick={handleDrawerClose}
              style={{ marginBottom: '-1.25rem', marginLeft: '1.2rem' }}
              icon={
                theme.direction === 'ltr' ? <ChevronLeft /> : <ChevronRight />
              }
            />
          </DrawerHeader>
          <Sidebar
            defaultActivatedId={defaultActivatedModule}
            sidebarData={sidebarData}
            manuallyActivatedId={currentActivatedModule}
            onChangeActivated={handleChangeActivated}
            barInnerWidth={drawerInnerWidth}
          />
          <div className="w-full flex pt-8 justify-center">
            {props.buttonField}
          </div>
        </div>
      </Drawer>
    </>
  ) : (
    <Sidebar
      defaultActivatedId={defaultActivatedModule}
      sidebarData={sidebarData}
      onChangeActivated={handleChangeActivated}
    />
  );
};
